/**
 * TaskGroupChildren Component
 *
 * Feature: 650-task-grouping
 *
 * Renders the expanded list of child tasks within a group.
 * Shows full task previews with photo thumbnails, similar to ungrouped tasks.
 */

import { Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import type { TaskGroupChildrenProps } from '@/types/task-groups';
import type { Task, TaskStatus } from '@/hooks/graphql/useTaskMutationsGraphQL';
import { usePhotoByIdGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';

/**
 * GraphQL query for fetching critique session data for task context
 */
const GET_CRITIQUE_SESSION_FOR_TASK = gql`
  query GetCritiqueSessionForTask($id: ID!) {
    critiqueSession(id: $id) {
      id
      globalId
      title
      collection {
        id
        title
        heroPhoto {
          id
          variants {
            id
            variantType
            storageUrl
            publicUrl
            width
            height
          }
        }
      }
    }
  }
`;

interface PhotoVariant {
  id: string;
  variantType: string;
  storageUrl: string;
  publicUrl: string;
  width: number;
  height: number;
}

interface CritiqueSessionForTaskData {
  critiqueSession: {
    id: string;
    globalId: string;
    title: string;
    collection: {
      id: string;
      title: string;
      heroPhoto?: {
        id: string;
        variants: PhotoVariant[];
      } | null;
    };
  };
}

/**
 * Get status badge color
 */
function getStatusColor(status: TaskStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-success/20 text-success';
    case 'dismissed':
      return 'bg-muted/20 text-muted';
    case 'pending':
    default:
      return 'bg-amber-500/20 text-amber-600 dark:text-amber-400';
  }
}

/**
 * Get status label
 */
function getStatusLabel(status: TaskStatus): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'dismissed':
      return 'Dismissed';
    case 'pending':
    default:
      return 'Pending';
  }
}

/**
 * Get action type display label
 * Feature 651: Added COMMENT_ON_N_PHOTOS
 */
function getActionTypeLabel(actionType: string): string {
  const labels: Record<string, string> = {
    VIEW_PHOTO: 'View Photo',
    COMMENT_ON_PHOTO: 'Comment on Photo',
    RATE_PHOTO: 'Rate Photo',
    REPLY_TO_COMMENT: 'Reply to Comment',
    COMMENT_ON_N_PHOTOS: 'Comment on Photos',  // Feature 651
    MANUAL: 'Custom Task',
  };
  return labels[actionType] || actionType.replace(/_/g, ' ').toLowerCase();
}

/**
 * Get progress display text for aggregate tasks
 * Feature 651: N Comments progress display
 */
function getProgressText(task: Task): string | null {
  if (!task.hasProgress || task.progressRequired === null) {
    return null;
  }
  return `${task.progressCurrent}/${task.progressRequired}`;
}

/**
 * Task preview for photo_in_critique target type
 * Shows the specific photo being worked on within the critique session
 */
function PhotoInCritiqueTaskPreview({
  task,
  onNavigateToTask,
}: {
  task: Task;
  onNavigateToTask: (task: Task) => void;
}) {
  // Extract photoId from targetContext
  const photoId = task.targetContext && 'photoId' in task.targetContext
    ? String(task.targetContext.photoId)
    : null;

  // Fetch the photo data
  const { data: photoData, isLoading: photoLoading } = usePhotoByIdGraphQL(photoId ?? undefined);

  // Fetch critique session for title
  const { data: sessionData, loading: sessionLoading } = useQuery<CritiqueSessionForTaskData>(
    GET_CRITIQUE_SESSION_FOR_TASK,
    {
      variables: { id: task.targetId || 'skip-placeholder' },
      skip: !task.targetId,
      fetchPolicy: 'cache-first',
    }
  );

  const sessionTitle = sessionData?.critiqueSession?.title || '';
  const isLoading = photoLoading || sessionLoading;

  return (
    <div
      className={`p-4 flex items-start gap-4 ${
        task.status === 'completed'
          ? 'opacity-60'
          : task.status === 'dismissed'
          ? 'opacity-40'
          : ''
      }`}
    >
      {/* Photo Thumbnail */}
      <div className="w-16 h-16 bg-surface-2 rounded-lg overflow-hidden flex-shrink-0 relative">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : photoData?.variants ? (
          <AdaptivePhoto
            photo={photoData}
            alt="Task target photo"
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {/* Status indicator overlay */}
        {task.status === 'completed' && (
          <div className="absolute inset-0 bg-success/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>

      {/* Task Info */}
      <div className="flex-1 min-w-0">
        {/* Action Type + Status */}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-sm font-medium text-foreground">
            {getActionTypeLabel(task.actionType)}
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
            {getStatusLabel(task.status)}
          </span>
        </div>

        {/* Description - task description, photo description, or session title */}
        {(task.description || photoData?.description || sessionTitle) && (
          <div className="text-sm text-muted truncate">
            {task.description || photoData?.description || sessionTitle}
          </div>
        )}
      </div>

      {/* View Link */}
      <Link
        to={task.targetUrl}
        onClick={(e) => {
          e.stopPropagation();
          onNavigateToTask(task);
        }}
        className="px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors flex-shrink-0"
      >
        View
      </Link>
    </div>
  );
}

/**
 * Get the best thumbnail URL from photo variants
 */
function getThumbnailUrl(variants: PhotoVariant[] | undefined): string | null {
  if (!variants || variants.length === 0) return null;
  // Prefer small or medium variants for thumbnails
  const small = variants.find(v => v.variantType === 'small');
  if (small) return small.publicUrl;
  const medium = variants.find(v => v.variantType === 'medium');
  if (medium) return medium.publicUrl;
  // Fall back to first available
  return variants[0]?.publicUrl || null;
}

/**
 * Preview for critique_session target type (e.g., COMMENT_ON_N_PHOTOS tasks)
 * Feature 651: Shows "Comment on N photos in X" with hero photo
 */
function CritiqueSessionTaskPreview({
  task,
  onNavigateToTask,
}: {
  task: Task;
  onNavigateToTask: (task: Task) => void;
}) {
  const progressText = getProgressText(task);

  // Fetch critique session for title and hero photo
  const { data: sessionData, loading: sessionLoading } = useQuery<CritiqueSessionForTaskData>(
    GET_CRITIQUE_SESSION_FOR_TASK,
    {
      variables: { id: task.targetId || 'skip-placeholder' },
      skip: !task.targetId,
      fetchPolicy: 'cache-first',
    }
  );

  const sessionTitle = sessionData?.critiqueSession?.title || 'Discussion';
  const heroPhoto = sessionData?.critiqueSession?.collection?.heroPhoto;
  const heroUrl = getThumbnailUrl(heroPhoto?.variants);
  const isLoading = sessionLoading;

  // Build the task title: "Comment on N photos in X"
  const targetCount = task.progressRequired ?? 0;
  const taskTitle = `Comment on ${targetCount} photo${targetCount !== 1 ? 's' : ''} in ${sessionTitle}`;

  return (
    <div
      className={`p-4 flex items-start gap-4 ${
        task.status === 'completed'
          ? 'opacity-60'
          : task.status === 'dismissed'
          ? 'opacity-40'
          : ''
      }`}
    >
      {/* Hero Photo Thumbnail */}
      <div className="w-16 h-16 bg-surface-2 rounded-lg overflow-hidden flex-shrink-0 relative">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : heroUrl ? (
          <img
            src={heroUrl}
            alt={sessionTitle}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {/* Status indicator overlay */}
        {task.status === 'completed' && (
          <div className="absolute inset-0 bg-success/30 flex items-center justify-center">
            <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {task.status === 'dismissed' && (
          <div className="absolute inset-0 bg-muted/30 flex items-center justify-center">
            <svg className="w-6 h-6 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}
        {/* Progress bar for aggregate tasks */}
        {task.status === 'pending' && task.progressPercent !== null && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{ width: `${Math.min(100, task.progressPercent)}%` }}
            />
          </div>
        )}
      </div>

      {/* Task Info */}
      <div className="flex-1 min-w-0">
        {/* Task Title + Status */}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-sm font-medium text-foreground">
            {taskTitle}
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
            {getStatusLabel(task.status)}
          </span>
        </div>

        {/* Progress indicator */}
        {progressText && (
          <div className="text-sm text-muted mb-1">
            {task.progressCurrent} of {task.progressRequired} photos commented
          </div>
        )}
      </div>

      {/* View Link */}
      <Link
        to={task.targetUrl}
        onClick={(e) => {
          e.stopPropagation();
          onNavigateToTask(task);
        }}
        className="px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors flex-shrink-0"
      >
        Go to Critique
      </Link>
    </div>
  );
}

/**
 * Generic task preview for other target types
 * Feature 651: Extended with progress display for aggregate tasks
 */
function GenericTaskPreview({
  task,
  onNavigateToTask,
}: {
  task: Task;
  onNavigateToTask: (task: Task) => void;
}) {
  const progressText = getProgressText(task);

  return (
    <div
      className={`p-4 flex items-start gap-4 ${
        task.status === 'completed'
          ? 'opacity-60'
          : task.status === 'dismissed'
          ? 'opacity-40'
          : ''
      }`}
    >
      {/* Icon with optional progress indicator */}
      <div className="w-16 h-16 bg-surface-2 rounded-lg flex flex-col items-center justify-center flex-shrink-0 relative">
        {task.status === 'completed' ? (
          <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : task.status === 'dismissed' ? (
          <svg className="w-6 h-6 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : progressText ? (
          /* Feature 651: Show progress for aggregate tasks */
          <span className="text-sm font-semibold text-amber-500">{progressText}</span>
        ) : (
          <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        {/* Progress bar for aggregate tasks */}
        {progressText && task.status === 'pending' && task.progressPercent !== null && (
          <div className="absolute bottom-1 left-1 right-1 h-1 bg-muted/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{ width: `${Math.min(100, task.progressPercent)}%` }}
            />
          </div>
        )}
      </div>

      {/* Task Info */}
      <div className="flex-1 min-w-0">
        {/* Action Type + Status + Progress */}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-sm font-medium text-foreground">
            {getActionTypeLabel(task.actionType)}
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
            {getStatusLabel(task.status)}
          </span>
          {/* Feature 651: Progress badge for aggregate tasks */}
          {progressText && task.status === 'pending' && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-primary/10 text-primary">
              {progressText} photos
            </span>
          )}
        </div>

        {/* Description */}
        {task.description && (
          <div className="text-sm text-muted truncate">
            {task.description}
          </div>
        )}
      </div>

      {/* View Link */}
      <Link
        to={task.targetUrl}
        onClick={(e) => {
          e.stopPropagation();
          onNavigateToTask(task);
        }}
        className="px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors flex-shrink-0"
      >
        View
      </Link>
    </div>
  );
}

/**
 * Renders the appropriate preview based on task target type
 */
function TaskChildPreview({
  task,
  onNavigateToTask,
}: {
  task: Task;
  onNavigateToTask: (task: Task) => void;
}) {
  // For photo_in_critique, show the photo preview
  if (task.targetType === 'photo_in_critique') {
    return <PhotoInCritiqueTaskPreview task={task} onNavigateToTask={onNavigateToTask} />;
  }

  // Feature 651: For critique_session (COMMENT_ON_N_PHOTOS), show session title
  if (task.targetType === 'critique_session') {
    return <CritiqueSessionTaskPreview task={task} onNavigateToTask={onNavigateToTask} />;
  }

  // For other types, show generic preview
  return <GenericTaskPreview task={task} onNavigateToTask={onNavigateToTask} />;
}

export function TaskGroupChildren({ group, onNavigateToTask }: TaskGroupChildrenProps) {
  return (
    <div className="divide-y divide-border/50">
      {group.tasks.map(task => (
        <TaskChildPreview
          key={task.id}
          task={task}
          onNavigateToTask={onNavigateToTask}
        />
      ))}
    </div>
  );
}
